// Copy of NaturalLanguageSearchPage from components/transactionSearchPage/TransactionSearchPage.js for the Payments - Beta tab.
import React, { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import * as S from "sanctuary";
import styled from "styled-components";
import { Redirect } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Box } from "@thecb/components";
import NaturalLanguageSearch from "@/components/transactionSearchPage/NaturalLanguageSearch";
import FiltersToggle from "@/components/transactionSearchPage/FiltersToggle";
import Alert from "@/components/generic/Alert";
import { ALERT_LEVELS } from "@/components/generic/Alert/constants";

import {
  FILTER_CATEGORY_DISPLAY_TEXT,
  isMultiValueFilter,
} from "@/components/transactionSearchPage/FiltersForm";
import FilterPill from "@/components/transactionSearchPage/FilterPill";
import DateFilterPillV2Beta from "./DateFilterPillV2Beta";
import PaymentsBetaAnnouncement from "./PaymentsBetaAnnouncement";
import ClearFiltersButton from "@/components/transactionSearchPage/ClearFiltersButton";
import CHIP_ICONS from "@/components/transactionSearchPage/chipIcons";

import TransactionSearchResultsV2Beta from "@/components/transactionSearchResultsV2Beta";
import {
  BetaAnnouncementRow,
  SearchPageContainer,
  StableScrollbarGutter,
  NaturalLanguageSearchPageContent,
  InlineAlertScope,
  SearchRow,
  SearchFieldSlot,
  DateFilterSlot,
} from "@/components/transactionSearchPage/TransactionSearchPage.styled";
import {
  FILTERS_CONTAINER_ID,
  FILTERS_VIEWS,
  hasPopulatedFilters,
  isFiltersPanelVisible,
  nextFiltersView,
  visibleFilters,
} from "@/components/transactionSearchPage/filterPresentation";
import useSearchBarSearchLifecycle from "@/components/transactionSearchPage/useSearchBarSearchLifecycle";

// Pill labels keyed by criteria form field; filters use
// FILTER_CATEGORY_DISPLAY_TEXT.
const CRITERIA_LABELS = {
  accountNumber: "Account number",
  amount: "Amount",
  bankAccountHolderName: "Bank account holder name",
  bankLastFour: "Bank last four",
  cardholderName: "Cardholder name",
  cardLastFour: "Credit card last four",
  transactionId: "Transaction ID",
};

/**
 * stampRange
 *
 * Day-granular stamp for comparing date ranges, since users only ever see
 * days.
 */
const stampRange = (start, end) => {
  const day = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");
  return `${day(start)}|${day(end)}`;
};

const toErrorMessage = (maybeError) => {
  if (!maybeError || !S.isJust(maybeError)) return null;
  const value = maybeError.value;
  if (typeof value === "string") return value;
  if (value && typeof value.message === "string") return value.message;
  return "Something went wrong with your search. Please try again.";
};

// The search field's root carries the row's 1rem bottom margin
// (NaturalLanguageSearch); with the field hidden, the row supplies it.
const SearchRowV2Beta = styled(SearchRow)`
  ${({ $withoutSearchField }) => ($withoutSearchField ? "margin-bottom: 1rem;" : "")}
`;

const NaturalLanguageSearchPageV2Beta = ({
  actions,
  forms,
  requestSearch,
  setNlSearchDates,
  resetSearchDatesToDefault,
  clearFilters,
  openDatesCalendar,
  selectDateFilter,
  selectedDateFilter,
  filterChoices,
  searchStartDate,
  searchEndDate,
  searchRequestPending,
  searchRequestError,
  translateRequestPending,
  translateRequestError,
  translateResult,
  translateRequest,
  clearTranslate,
  translateResultApplied = false,
  markTranslateResultApplied,
  userTimezone,
  filtersView = FILTERS_VIEWS.ALL,
  setFiltersView,
  lastSettledSearch = null,
}) => {
  // On the beta tab this flag only shows or hides the AI search bar; filters
  // and results render either way.
  const flags = useFlags();
  const naturalLanguageSearchEnabled =
    flags?.["enable-natural-language-search-rev-m"];
  const criteriaForm = forms.criteriaForm.fields;
  const criteriaActions = actions.forms.criteriaForm.actions;
  const filtersForm = forms.filtersForm.fields;
  const filtersActions = actions.forms.filtersForm.actions;

  const criteriaKeys = Object.keys(criteriaForm);
  const filterKeys = Object.keys(filterChoices ?? {});

  const handleCriteriaCommit = (key) => (value) => {
    criteriaActions.fields[key]?.set(value);
    requestSearch();
  };

  const handleFilterCommit = (key) => (value) => {
    filtersActions.fields[key]?.set(value);
    requestSearch();
  };

  // Every filter the panel can show, date range excluded;
  // `filterPresentation` decides which render, and in what order.
  const panelFilters = [
    ...criteriaKeys.map((key) => ({
      key: `criteria-${key}`,
      dataQa: `nl-criteria-pill-${key}`,
      label: CRITERIA_LABELS[key] ?? key,
      value: criteriaForm[key]?.rawValue ?? "",
      onSelect: handleCriteriaCommit(key),
      icon: CHIP_ICONS[key],
    })),
    ...filterKeys.map((key) => ({
      key: `filter-${key}`,
      dataQa: `nl-filter-pill-${key}`,
      label: FILTER_CATEGORY_DISPLAY_TEXT[key] ?? key,
      value: filtersForm[key]?.rawValue ?? "",
      options: filterChoices[key],
      onSelect: handleFilterCommit(key),
      icon: CHIP_ICONS[key],
      multiSelect: isMultiValueFilter(key),
    })),
  ];

  const filtersPopulated = hasPopulatedFilters(panelFilters);

  const hasParameters = filtersPopulated || !!searchStartDate || !!searchEndDate;

  // Whether the range has changed since the last search ran. A dirty range is
  // reason enough to search on a blank query: there is nothing to translate,
  // but there is a new range to search.
  const currentRangeRef = useRef(stampRange(searchStartDate, searchEndDate));
  currentRangeRef.current = stampRange(searchStartDate, searchEndDate);
  const lastSearchedRangeRef = useRef(currentRangeRef.current);
  const dateRangeDirty = currentRangeRef.current !== lastSearchedRangeRef.current;

  // Snapshot on the pending flag, not at the request site: the translate flow
  // writes dates and fires the search before this re-renders, so only the
  // pending render sees every date the search used.
  useEffect(() => {
    if (searchRequestPending) {
      lastSearchedRangeRef.current = currentRangeRef.current;
    }
  }, [searchRequestPending]);

  const translateErrorMessage = toErrorMessage(translateRequestError);
  const searchErrorMessage = toErrorMessage(searchRequestError);
  // Prefer the translate error; the two rarely overlap.
  const errorMessage = translateErrorMessage || searchErrorMessage;
  const errorTitle = translateErrorMessage
    ? "Couldn't translate your query"
    : "Search failed";
  // The page owns the alert's placement and dismissal. Dismissal keys off the
  // error value, not its text: each failure is a fresh Just, so a repeat with
  // the same message re-opens the alert.
  const currentError = translateErrorMessage
    ? translateRequestError
    : searchErrorMessage
      ? searchRequestError
      : null;
  const [dismissedError, setDismissedError] = useState(null);
  const activeErrorMessage =
    errorMessage && currentError !== dismissedError ? errorMessage : null;
  const resultPayload = S.isJust(translateResult) ? translateResult.value : null;

  const { clearQueryToken, searchBarSearchPending, submitQuery, runExistingSearch } =
    useSearchBarSearchLifecycle({
      // An applied result stays in the store across tab navigation; handing it
      // back after a remount would re-apply it and re-run its search.
      resultPayload: translateResultApplied ? null : resultPayload,
      criteriaActions,
      filtersActions,
      filterChoices,
      setNlSearchDates,
      resetSearchDatesToDefault,
      selectDateFilter,
      userTimezone,
      requestSearch,
      clearTranslate,
      translateRequest,
      filtersPopulated,
      lastSettledSearch,
      filtersView,
      setFiltersView,
    });

  // Runs after the lifecycle's own effect has applied the result.
  useEffect(() => {
    if (resultPayload && !translateResultApplied) markTranslateResultApplied();
  }, [resultPayload, translateResultApplied, markTranslateResultApplied]);

  // Freeze the chips and date filter for the round trip instead of wiping them.
  // `searchRequestPending` covers every /payments request, so also count the
  // lifecycle's pending state, or a paginated search unlocks the page early.
  const searchInFlight =
    !!translateRequestPending || !!searchRequestPending || !!searchBarSearchPending;

  // The reset dispatch is synchronous, so `requestSearch` already sees the
  // restored range.
  const handleDateReset = () => {
    resetSearchDatesToDefault();
    requestSearch();
  };

  // `clearFilters` also resets the date range; don't reset it again here.
  const handleClearFilters = () => {
    clearFilters();
    requestSearch();
  };

  return (
    <SearchPageContainer>
      <StableScrollbarGutter />
      <BetaAnnouncementRow>
        <PaymentsBetaAnnouncement />
      </BetaAnnouncementRow>
      <NaturalLanguageSearchPageContent
        $align="flex-start"
        data-qa="nl-search-page-content"
        data-testid="nl-search-page-content"
      >
        <SearchRowV2Beta $withoutSearchField={!naturalLanguageSearchEnabled}>
          {naturalLanguageSearchEnabled && (
            <SearchFieldSlot>
              <NaturalLanguageSearch
                pending={searchInFlight}
                error={activeErrorMessage}
                hasResult={!!resultPayload}
                onSubmit={submitQuery}
                onExecuteSearch={runExistingSearch}
                hasParameters={hasParameters}
                dateRangeDirty={dateRangeDirty}
                clearQueryToken={clearQueryToken}
              />
            </SearchFieldSlot>
          )}
          <DateFilterSlot
            $dimmed={searchInFlight}
            aria-busy={searchInFlight}
            data-qa="nl-date-filter-slot"
            data-testid="nl-date-filter-slot"
          >
            <DateFilterPillV2Beta
              label="Date"
              dataQa="nl-filter-pill-date"
              icon={CHIP_ICONS.date}
              size="large"
              selectedDateFilter={selectedDateFilter}
              selectDateFilter={selectDateFilter}
              searchStartDate={searchStartDate}
              searchEndDate={searchEndDate}
              openCalendar={openDatesCalendar}
              onApply={requestSearch}
              onReset={handleDateReset}
              disabled={searchInFlight}
            />
          </DateFilterSlot>
          <FiltersToggle
            view={filtersView}
            hasPopulatedFilters={filtersPopulated}
            onToggle={() => setFiltersView?.(nextFiltersView(filtersView))}
            controlsId={FILTERS_CONTAINER_ID}
          />
        </SearchRowV2Beta>
        {activeErrorMessage && (
          <InlineAlertScope data-qa="nl-search-error" data-testid="nl-search-error">
            <Alert
              alertLevel={ALERT_LEVELS.ERROR}
              title={errorTitle}
              text={activeErrorMessage}
              onClose={() => setDismissedError(currentError)}
            />
          </InlineAlertScope>
        )}
        {isFiltersPanelVisible(filtersView, filtersPopulated) && (
          <Box
            id={FILTERS_CONTAINER_ID}
            padding="0 0 1rem 0"
            extraStyles={`width: 100%; display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem;${
              searchInFlight ? " opacity: 0.6;" : ""
            }`}
            aria-busy={searchInFlight}
            data-qa="nl-search-parameter-pills"
            data-testid="nl-search-parameter-pills"
          >
            {visibleFilters(panelFilters, filtersView).map((filter) => (
              <FilterPill
                key={filter.key}
                dataQa={filter.dataQa}
                label={filter.label}
                value={filter.value}
                options={filter.options}
                onSelect={filter.onSelect}
                icon={filter.icon}
                multiSelect={filter.multiSelect}
                disabled={searchInFlight}
              />
            ))}
            <ClearFiltersButton
              dataQa="nl-clear-filters"
              onClick={handleClearFilters}
              disabled={searchInFlight}
            />
          </Box>
        )}
        <TransactionSearchResultsV2Beta />
      </NaturalLanguageSearchPageContent>
    </SearchPageContainer>
  );
};

// Redirects to /payments without `revm-payments-beta-tab`. Calls no hooks
// besides `useFlags`, so the page's own hooks keep their order when the flag
// flips.
const TransactionSearchPageV2Beta = (props) => {
  const flags = useFlags();
  const paymentsBetaTabEnabled = flags?.["revm-payments-beta-tab"];

  return paymentsBetaTabEnabled ? (
    <NaturalLanguageSearchPageV2Beta {...props} />
  ) : (
    <Redirect to="/payments" />
  );
};

export default TransactionSearchPageV2Beta;
